import React, { forwardRef, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Paper,
  Button,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosWithToken from "../../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL, DISABLED_MODULE } from "../../../global";
import { ClassicTable } from "./ClassicTable";

const fetchAddresses = async (addressIds, shippingAddressIds) => {
  const sqlQuery = {
    query: `SELECT * FROM addresses WHERE id IN (${addressIds || 0},${
      shippingAddressIds || 0
    })`,
  };

  const res = await axiosWithToken.post(BASE_URL + `dynamic`, sqlQuery, {
    headers: { requestType: "fetchData" },
  });
  return res.data;
};

export const onSubmitAddress = async (
  allAddresses,
  setShippingAddressIds,
  setAddressIds
) => {
  try {
    const billingAddresses = allAddresses.map(({ billing }) => ({
      id: billing.id,
      address: billing.address,
    }));

    const simplifiedAddresses = allAddresses.map(
      ({ id, address, coordinates }) => ({
        id,
        address,
        coordinates,
      })
    );

    const combinedAddresses = [...billingAddresses, ...simplifiedAddresses];

    const oldAddress = combinedAddresses.filter(
      (address) => address.id !== null
    );

    let addressIdState = "";
    let shippingAddressIdState = "";

    const newBillingAddresses = billingAddresses
      .filter((address) => address.id === null)
      .map(({ address }) => ({ address }));

    const newSimplifiedAddresses = simplifiedAddresses
      .filter((address) => address.id === null)
      .map(({ address, coordinates }) => ({ address, coordinates }));

    const shippingPromise =
      newBillingAddresses.length > 0
        ? axiosWithToken.post(BASE_URL + "addresses/bulk", newBillingAddresses)
        : Promise.resolve(null);

    const addressPromise =
      newSimplifiedAddresses.length > 0
        ? axiosWithToken.post(
            BASE_URL + "addresses/bulk",
            newSimplifiedAddresses
          )
        : Promise.resolve(null);

    const [shippingResponse, addressResponse] = await Promise.all([
      shippingPromise,
      addressPromise,
    ]);

    if (shippingResponse) {
      const insertId = shippingResponse.data.insertId;
      const lengthOfArray = newBillingAddresses.length;

      const result = Array.from(
        { length: lengthOfArray },
        (_, index) => insertId + index
      ).join(",");

      shippingAddressIdState = result;
      setShippingAddressIds((prevIds) =>
        prevIds ? `${prevIds},${result}` : result
      );
    }

    if (addressResponse) {
      const insertId = addressResponse.data.insertId;
      const lengthOfArray = newSimplifiedAddresses.length;

      const result = Array.from(
        { length: lengthOfArray },
        (_, index) => insertId + index
      ).join(",");

      addressIdState = result;
      setAddressIds((prevIds) => (prevIds ? `${prevIds},${result}` : result));
    }

    if (oldAddress.length > 0) {
      await Promise.all(
        oldAddress.map(async (address) => {
          let response = await axiosWithToken.patch(
            BASE_URL + `addresses/${address.id}`,
            address
          );
        })
      );
    }

    // toast.success("Addresses saved successfully");

    return [addressIdState, shippingAddressIdState];
  } catch (error) {
    // Handle errors
    console.error("Error in onSubmit:", error);
  }
};

export const deleteMarkedAddresses = async (deletedAddresses) => {
  try {
    if (deletedAddresses.length === 0) return;

    const deletePromises = [];

    deletedAddresses.forEach(({ addressID, billingID }) => {
      if (addressID) {
        deletePromises.push(
          axiosWithToken.delete(BASE_URL + `addresses/${addressID}`)
        );
      }
      if (billingID) {
        deletePromises.push(
          axiosWithToken.delete(BASE_URL + `addresses/${billingID}`)
        );
      }
    });

    await Promise.all(deletePromises);
    console.log("Deleted addresses successfully");
  } catch (error) {
    console.error("Error deleting addresses:", error);
    throw error;
  }
};

const MultiAddressInput = (
  {
    addressIds,
    shippingAddressIds,
    setAddressIds,
    setShippingAddressIds,
    allAddresses,
    setAllAddresses,
    params,
    deletedAddresses, // NEW
    setDeletedAddresses, // NEW
    permissions
  },
  ref
) => {
  useEffect(() => {
    const fetchData = async () => {
      if (addressIds && shippingAddressIds) {
        const addresses1 = await fetchAddresses(addressIds, shippingAddressIds);
        const shippingAddressArray = addressIds.split(",").map(Number);
        const billingAddressArray = shippingAddressIds.split(",").map(Number);

        const result = shippingAddressArray.map((id, index) => {
          const address = addresses1.find(
            (a) => a.id !== undefined && a.id === id
          );
          const billingId = billingAddressArray[index];
          const billingInfo = addresses1.find((a) => a.id === billingId);

          const addressId = address ? address.id : null;
          const addressAddress = address ? address?.address : "";
          const coordinates = address ? address?.coordinates : null;

          return {
            id: addressId,
            address: addressAddress,
            billing: billingInfo
              ? { id: billingInfo.id, address: billingInfo.address }
              : { id: null, address: "" },
            coordinates: coordinates,
          };
        });

        setAllAddresses(result);
      }
    };

    fetchData();
  }, [addressIds, shippingAddressIds]);
  const handleAddAddress = () => {
    setAllAddresses((prevAddresses) => [
      ...prevAddresses,
      {
        id: null,
        address: "",
        billing: { id: null, address: "" },
        coordinates: null,
      },
    ]);
  };

  const handleDeleteAddress = (index, addressID, billingID) => {
    // Just mark for deletion and remove from UI
    if (addressID !== null || billingID !== null) {
      setDeletedAddresses((prev) => [
        ...prev,
        {
          addressID: addressID,
          billingID: billingID,
        },
      ]);
    }

    // Remove from local state immediately (UI update)
      setAllAddresses((prevAddresses) => {
        const updatedAddresses = [...prevAddresses];
        updatedAddresses.splice(index, 1);
        return updatedAddresses;
      });

    // Update ID strings in state
    if (addressID) {
      setAddressIds((prevIds) =>
        prevIds
          .split(",")
          .filter((id) => id && id !== String(addressID))
          .join(",")
      );
    }
    
    if (billingID) {
      setShippingAddressIds((prevIds) =>
        prevIds
          .split(",")
          .filter((id) => id && id !== String(billingID))
          .join(",")
      );
    }
  };

  const handleAddressChange = (index, value) => {
    setAllAddresses((prevAddresses) => {
      const updatedAddresses = [...prevAddresses];
      updatedAddresses[index].address = value;
      return updatedAddresses;
    });
  };

  const handleBillingChange = (index, value) => {
    setAllAddresses((prevAddresses) => {
      const updatedAddresses = [...prevAddresses];
      updatedAddresses[index].billing.address = value;
      return updatedAddresses;
    });
  };

  const handleCoordinatesChange = (index, value) => {
    setAllAddresses((prevAddresses) => {
      const updatedAddresses = [...prevAddresses];
      updatedAddresses[index].coordinates = value;
      return updatedAddresses;
    });
  };

  return (
    <ClassicTable>
      <div style={{display:"flex" , justifyContent:"flex-end", marginBottom:"15px"}}>
      <Button
        id="client_edit_addaddrbtn"
        variant="contained"
        size="small"
        onClick={handleAddAddress}
      >
        Add Address
      </Button>
      </div>
      <Table>
        <TableHead>
          <TableRow style={{ backgroundColor: "rgb(245 245 245)" }}>
            <TableCell style={{ borderRight: "none" }}>
              Client Address
            </TableCell>
            {/* {!DISABLED_MODULE?.includes("CoordinatesAddress") && (
              <TableCell style={{ borderRight: "none" }}>
                Certificate / Billing Address Coordinates
              </TableCell>
            )} */}
            {/* <TableCell>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  borderLeft: "none",
                  alignItems: "center",
                }}
              >
                Shipping Address
                <Button
                  id="client_edit_addaddrbtn"
                  variant="contained"
                  size="small"
                  onClick={handleAddAddress}
                >
                  Add Address
                </Button>
              </div>
            </TableCell> */}
             {(permissions.delete || permissions.allAccess) && (
              <TableCell style={{ fontWeight: "bold" }}>Action</TableCell>
             )}
          </TableRow>
        </TableHead>
        <TableBody>
          {allAddresses?.map((address, index) => (
            <TableRow key={address.id || `new-${index}`}>
              <TableCell>
                <TextField
                  id={`address_${address.id}`}
                  size="small"
                  fullWidth
                  inputProps={{
                    autoComplete: "off",
                  }}
                  label={`Address ${index + 1}`}
                  value={address.address}
                  onChange={(e) => handleAddressChange(index, e.target.value)}
                />
              </TableCell>
              {/* {!DISABLED_MODULE?.includes("CoordinatesAddress") && (
                <TableCell>
                  <TextField
                    // id={`address_${address.id}`}
                    size="small"
                    fullWidth
                    label={`Latitude , Longitude`}
                    value={address.coordinates}
                    inputProps={{
                      autoComplete: "off",
                    }}
                    onChange={(e) =>
                      handleCoordinatesChange(index, e.target.value)
                    }
                  />
                </TableCell>
              )} */}
              {/* <TableCell>
                <TextField
                  id={`billing_${address.id}`}
                  size="small"  
                  fullWidth
                  inputProps={{
                    autoComplete: "off",
                  }}
                  label={`Billing ${index + 1}`}
                  value={address.billing.address}
                  onChange={(e) => handleBillingChange(index, e.target.value)}
                />
              </TableCell> */}
              {(permissions.delete || permissions.allAccess) && (
              <TableCell>
                <IconButton
                  onClick={() => {
                    const confirmation = window.confirm(
                      "Are you sure you want to delete this address?"
                    );
                    if (confirmation) {
                      handleDeleteAddress(
                        index,
                        address.id,
                        address.billing.id
                      );
                    }
                  }}
                >
                  <DeleteIcon
                    id={`delete_${address.id}`}
                    style={{ cursor: "pointer" }}
                  />
                </IconButton>
              </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ClassicTable>
  );
};

export default MultiAddressInput;
